import React, { useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GROUPED_MIN_ITEMS } from '../data/navigation';
import { isAdmin, isDistributorAdmin } from '../utils/roles';
import {
  adminWorkspaceForPath,
  canSwitchWorkspace,
  sidebarFirstName,
  visibleGroups,
  workspaceGroups,
} from '../utils/sidebarNavigation';
import { useT } from '../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';
import useHelpUnread from '../hooks/useHelpUnread';
import '../styles/workspace-navigation.css';

export { visibleGroups } from '../utils/sidebarNavigation';

const helpPath = (path) => ['/help-center', '/distributor/help'].includes(path);
const roleLabel = (user) =>
  isAdmin(user)
    ? 'Admin'
    : user?.role === 'master_distributor'
      ? 'Distributor'
      : user?.role === 'clinic'
        ? 'Clinic'
        : user?.role === 'sales_rep'
          ? 'Sales Rep'
          : 'Account';

export default function Sidebar() {
  const { user, logout, sessionVersion } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const unread = useHelpUnread(user, sessionVersion);
  const switching = canSwitchWorkspace(user);
  const scope = adminWorkspaceForPath(location.pathname);
  const groups = workspaceGroups(user, location.pathname);
  const showHeaders =
    groups.reduce((total, group) => total + group.items.length, 0) >= GROUPED_MIN_ITEMS;
  const name = sidebarFirstName(user) || t('Account');
  const owner = JSON.stringify([
    user?._id || user?.id || user?.email || null,
    user?.role,
    user?.adminWorkspace,
    sessionVersion,
  ]);
  const remembered = useRef(null);
  if (remembered.current?.owner !== owner) {
    remembered.current = { owner, medeffects: '/', distributor: '/admin/distributor-customers' };
  }
  useEffect(() => {
    if (
      switching &&
      visibleGroups(user).some((group) =>
        group.items.some((item) => item.path === location.pathname)
      )
    )
      remembered.current[scope] = location.pathname;
  }, [location.pathname, owner, scope, switching, user]);

  const chooseWorkspace = (next) => {
    if (!switching || next === scope) return;
    const destination = remembered.current[next];
    const allowed = visibleGroups(user)
      .filter((group) => (group.label === 'Distributor') === (next === 'distributor'))
      .flatMap((group) => group.items);
    if (allowed.some((item) => item.path === destination)) navigate(destination);
  };
  const handleLogout = () => {
    const distributor = user?.role === 'master_distributor' || isDistributorAdmin(user);
    logout();
    navigate(distributor ? '/distributor/login' : '/login', { replace: true });
  };

  return (
    <aside className="me-workspace-sidebar">
      <header className="me-workspace-brand">
        <img src="/assets/logo.png" alt="MedEffects" />
        <span className="me-workspace-role" title={t(roleLabel(user))}>
          {t(roleLabel(user))}
        </span>
      </header>
      {switching && (
        <div className="me-workspace-switch" role="group" aria-label={t('Workspace navigation')}>
          {[
            ['medeffects', 'MedEffects', 'medical_services'],
            ['distributor', 'Distributor', 'storefront'],
          ].map(([key, label, icon]) => (
            <button
              key={key}
              type="button"
              aria-pressed={scope === key}
              onClick={() => chooseWorkspace(key)}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                {icon}
              </span>
              <span>{t(label)}</span>
            </button>
          ))}
        </div>
      )}
      <nav className="me-workspace-nav" aria-label={t('Navigation')}>
        {groups.map((group) => {
          const scoped = switching && group.label === 'Distributor';
          return (
            <section className="me-workspace-group" key={group.label}>
              {showHeaders && (
                <h2 className="me-workspace-group-title">
                  {t(scoped ? 'Distributor Navigation' : group.label)}
                </h2>
              )}
              {group.items.map((item) => {
                const badge = helpPath(item.path) ? unread : item.badge;
                const label = scoped ? item.label.replace(/^Distributor /, '') : item.label;
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    aria-label={scoped ? t(item.label) : undefined}
                    end={item.path === '/' || item.path === '/distributor'}
                    className="me-workspace-link"
                  >
                    <span
                      className="material-symbols-outlined me-workspace-icon"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    <span className="me-workspace-link-label">{t(label)}</span>
                    {badge > 0 && (
                      <span className="me-workspace-unread">{badge > 99 ? '99+' : badge}</span>
                    )}
                  </NavLink>
                );
              })}
            </section>
          );
        })}
      </nav>
      <footer className="me-workspace-account">
        <span className="me-workspace-avatar" aria-hidden="true">
          {name.charAt(0).toUpperCase()}
        </span>
        <span className="me-workspace-first-name" title={name}>
          {name}
        </span>
        <LanguageToggle tone="dark" iconOnly className="me-workspace-utility" />
        <button
          type="button"
          className="me-workspace-utility"
          onClick={handleLogout}
          title={t('Logout')}
          aria-label={t('Logout')}
        >
          <span className="material-symbols-outlined" aria-hidden="true">
            logout
          </span>
        </button>
      </footer>
    </aside>
  );
}
